import { useEffect } from 'react'

interface MetaTagsConfig {
  title: string
  description: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogType?: string
  ogImage?: string
  ogUrl?: string
  canonical?: string
  author?: string
  // Override per-page indexing. Defaults to "index, follow" for organic
  // pages. Lead-gen / partner / bridge pages should pass "noindex, follow"
  // so they don't compete with the brand site for SEO.
  robots?: string
  // Twitter handle (without @) for site / creator attribution.
  twitterSite?: string
}

function setMetaTag(name: string, content: string, attribute: string = 'name') {
  if (!content) return
  let element = document.querySelector(
    `meta[${attribute}="${name}"]`,
  ) as HTMLMetaElement | null
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, name)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function setLink(rel: string, href: string) {
  if (!href) return
  let element = document.querySelector(
    `link[rel="${rel}"]`,
  ) as HTMLLinkElement | null
  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }
  element.setAttribute('href', href)
}

export function useMetaTags({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogImage,
  ogUrl,
  canonical,
  author,
  robots = 'index, follow',
  twitterSite,
}: MetaTagsConfig) {
  useEffect(() => {
    // Page title
    document.title = title

    // Standard meta tags
    setMetaTag('description', description)
    if (keywords) setMetaTag('keywords', keywords)
    if (author) setMetaTag('author', author)

    // Robots — per-page so lead-gen pages can opt out of indexing
    setMetaTag('robots', robots)

    // Resolve canonical / og:url. Default to the current page URL so
    // every page gets a canonical even if the caller didn't pass one.
    const resolvedUrl =
      canonical ||
      ogUrl ||
      (typeof window !== 'undefined' ? window.location.href : '')

    if (resolvedUrl) {
      setLink('canonical', resolvedUrl)
      setMetaTag('og:url', resolvedUrl, 'property')
    }

    // Open Graph
    setMetaTag('og:title', ogTitle || title, 'property')
    setMetaTag('og:description', ogDescription || description, 'property')
    setMetaTag('og:type', ogType, 'property')
    if (ogImage) {
      setMetaTag('og:image', ogImage, 'property')
      setMetaTag('twitter:image', ogImage)
    }

    // Twitter card
    setMetaTag('twitter:card', ogImage ? 'summary_large_image' : 'summary')
    setMetaTag('twitter:title', ogTitle || title)
    setMetaTag('twitter:description', ogDescription || description)
    if (twitterSite) setMetaTag('twitter:site', `@${twitterSite}`)
  }, [
    title,
    description,
    keywords,
    ogTitle,
    ogDescription,
    ogType,
    ogImage,
    ogUrl,
    canonical,
    author,
    robots,
    twitterSite,
  ])
}
